import { useMemo } from 'react';
import { useAdminCollections } from '../../context/AdminCollections';
import { getNominationRoundYear } from '../../utils/getNominationRoundYear';

/**
 * Hook to track a Council Member's nomination status and COI compliance.
 * * It calculates:
 * 1. The number of nominations specifically allocated to the user for coi declaration.
 * 2. The total pool of nominations at the "Recommended to Council" stage (Status 8).
 * 3. The completion status of COI declarations for that entire Council pool.
 */
export const useCouncilMemberNominations = (
  userDetails,
  isCouncilMember,
  shouldCalculate,
) => {
  const {
    nominationStateArray,
    nominationArray,
    coiDeclarationArray,
    candidateArray,
  } = useAdminCollections();

  return useMemo(() => {
    if (!shouldCalculate || !isCouncilMember || !userDetails?.id) {
      return {
        allocatedCount: 0,
        recommendedCount: 0,
        nominationsWithCOICount: 0,
        nominationsWithoutCOICount: 0,
      };
    }

    if (
      !nominationStateArray ||
      !nominationArray ||
      !coiDeclarationArray ||
      !candidateArray
    ) {
      return {
        allocatedCount: 0,
        recommendedCount: 0,
        nominationsWithCOICount: 0,
        nominationsWithoutCOICount: 0,
      };
    }

    const currentRound = getNominationRoundYear(new Date());

    const allocatedCount = nominationStateArray.filter((state) => {
      const councilMemberUserId =
        state.attributes?.councilMemberUserId || state.councilMemberUserId;
      return Number(councilMemberUserId) === Number(userDetails.id);
    }).length;

    const recommendedToCouncil = nominationArray.filter((nom) => {
      const statusId =
        nom.attributes?.nominationStatusTypeId || nom.nominationStatusTypeId;
      return Number(statusId) === 8;
    });

    const recommendedCount = recommendedToCouncil.length;

    let nominationsWithCOICount = 0;
    let nominationsWithoutCOICount = 0;

    recommendedToCouncil.forEach((nom) => {
      const nominationId = nom.id;

      const hasCOI = coiDeclarationArray.some((declaration) => {
        const attr = declaration.attributes;
        const dNomId = attr?.nominationId || declaration.nominationId;
        const dUserId = attr?.userId || declaration.userId;
        const dRound = attr?.nominationRound || declaration.nominationRound;

        return (
          Number(dNomId) === Number(nominationId) &&
          Number(dUserId) === Number(userDetails.id) &&
          Number(dRound) === Number(currentRound)
        );
      });

      if (hasCOI) {
        nominationsWithCOICount += 1;
      } else {
        nominationsWithoutCOICount += 1;
      }
    });

    return {
      allocatedCount,
      recommendedCount,
      nominationsWithCOICount,
      nominationsWithoutCOICount,
    };
  }, [
    shouldCalculate,
    isCouncilMember,
    userDetails?.id,
    nominationStateArray,
    nominationArray,
    coiDeclarationArray,
    candidateArray,
  ]);
};
